import React, { useRef, useState } from "react";
import { HiOutlineArrowPath } from "react-icons/hi2";
import HolographicEffects from "./HolographicEffects";
import { CardPreviewProvider } from "../../contexts/CardPreviewContext";

interface BusinessCardContainerProps {
  children?: React.ReactNode;
  frontContent?: React.ReactNode;
  backContent?: React.ReactNode;
  className?: string;
  isPreview?: boolean;
  showFlipButton?: boolean;
  onFlip?: (isFlipped: boolean) => void;
  isFlipping?: boolean;
  showEmailLogin?: boolean;
  showForgotPasswordForm?: boolean;
  frontCardDimensions?: {
    width?: number;
    height?: number;
    className?: string;
  };
  fullHeight?: boolean;
}

const LoyaltyCardContainer: React.FC<BusinessCardContainerProps> = ({
  children,
  frontContent,
  backContent,
  className = "",
  isPreview = false,
  showFlipButton = false,
  onFlip,
  isFlipping: externalIsFlipping = false,
  showEmailLogin = false,
  showForgotPasswordForm = false,
  frontCardDimensions,
  fullHeight = true,
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const [isFlipped, setIsFlipped] = useState(showEmailLogin);
  const [isFlipping, setIsFlipping] = useState(false);

  const currentIsFlipping = externalIsFlipping || isFlipping;
  const actuallyFlipped = showEmailLogin || isFlipped;
  const baseFlipRotation = actuallyFlipped ? 180 : 0;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || currentIsFlipping || actuallyFlipped) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePosition({ x, y });
  };

  const handleMouseEnter = () => {
    if (!currentIsFlipping) {
      setIsHovered(true);

      if (!actuallyFlipped && cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setMousePosition({ x: rect.width / 2, y: rect.height / 2 });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePosition({ x: 0, y: 0 });
  };

  const handleFlip = () => {
    if (isFlipping) return;

    setIsFlipping(true);

    setTimeout(() => {
      setIsFlipped((prev) => {
        const newFlipped = !prev;
        onFlip?.(newFlipped);
        return newFlipped;
      });
    }, 300);

    setTimeout(() => {
      setIsFlipping(false);
    }, 600);
  };

  const getCardTransform = () => {
    if (
      !cardRef.current ||
      !isHovered ||
      currentIsFlipping ||
      actuallyFlipped
    ) {
      return `perspective(1000px) rotateY(${baseFlipRotation}deg)`;
    }

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((mousePosition.y - centerY) / centerY) * -3;
    const rotateY =
      baseFlipRotation + ((mousePosition.x - centerX) / centerX) * 3;

    return `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.01, 1.01, 1.01)`;
  };

  const containerClassName =
    frontCardDimensions?.className ||
    `rounded-xl w-[calc(100vw-2rem)] max-w-[360px] sm:w-[360px] min-h-[500px] h-auto mx-auto flex flex-col bg-transparent`;

  const containerStyle = {
    ...(frontCardDimensions && {
      width: frontCardDimensions.width
        ? `${frontCardDimensions.width}px`
        : undefined,
      height: frontCardDimensions.height
        ? `${frontCardDimensions.height}px`
        : undefined,
    }),
    transform: getCardTransform(),
    transformStyle: "preserve-3d" as const,
    willChange: "transform" as const,
    touchAction: "pan-y" as const,
    transition: currentIsFlipping
      ? "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
      : isHovered
      ? "transform 0.15s ease-out"
      : "transform 0.3s ease-out",
  };

  return (
    <CardPreviewProvider isPreview={isPreview}>
      <div
        className={`overflow-hidden relative group flex items-center justify-center touch-pan-y p-6 ${
          !isPreview && fullHeight ? "min-h-screen" : ""
        } ${!isPreview ? " py-8 px-4" : ""}`}
        translate="no"
      >
        <HolographicEffects
          isHovered={isHovered}
          onMouseMove={() => {}}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={`${containerClassName} ${className}`}
        >
          <div
            ref={cardRef}
            className="relative"
            style={containerStyle}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="relative w-full h-full overflow-visible rounded-xl border border-gray-300 shadow-sm">
              {!(showEmailLogin || isFlipped) && (
                <div
                  className="w-full h-full"
                  style={{
                    backfaceVisibility: "hidden",
                    transformOrigin: "center center",
                    opacity: currentIsFlipping ? 0 : 1,
                    transform: currentIsFlipping ? "scale(0.95)" : "scale(1)",
                    transition: currentIsFlipping
                      ? "opacity 0.25s ease-out, transform 0.25s ease-out"
                      : "opacity 0.3s ease-in 0.25s, transform 0.3s ease-in 0.25s",
                  }}
                >
                  {frontContent || children}
                </div>
              )}

              {(showEmailLogin || isFlipped) && backContent && (
                <div
                  className="w-full h-full"
                  style={{
                    transformOrigin: "center center",
                    opacity: currentIsFlipping ? 0 : 1,
                    transform: `${
                      currentIsFlipping ? "scale(0.95)" : "scale(1)"
                    } rotateY(180deg)`,
                    transition: currentIsFlipping
                      ? "opacity 0.25s ease-out, transform 0.25s ease-out"
                      : "opacity 0.3s ease-in 0.25s, transform 0.3s ease-in 0.25s",
                  }}
                >
                  {backContent}
                </div>
              )}

              {(showEmailLogin || isFlipped) && !backContent && (
                <div
                  className="w-full h-full"
                  style={{
                    transformOrigin: "center center",
                    opacity: currentIsFlipping ? 0 : 1,
                    transform: `${
                      currentIsFlipping ? "scale(0.95)" : "scale(1)"
                    } rotateY(180deg)`,
                    transition: currentIsFlipping
                      ? "opacity 0.25s ease-out, transform 0.25s ease-out"
                      : "opacity 0.3s ease-in 0.25s, transform 0.3s ease-in 0.25s",
                  }}
                >
                  {frontContent || children}
                </div>
              )}
            </div>

            {showFlipButton && backContent && !isPreview && (
              <button
                onClick={handleFlip}
                disabled={isFlipping}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-sm transition-all duration-200 hover:shadow-lg disabled:opacity-50 z-10"
                title={isFlipped ? "Show front" : "Show back"}
              >
                <HiOutlineArrowPath
                  className={`w-5 h-5 transition-transform duration-300 ${
                    isFlipped ? "rotate-180" : ""
                  }`}
                />
              </button>
            )}
          </div>
        </HolographicEffects>

        {!isPreview && (
          <div
            className="absolute inset-0 bg-gradient-to-br from-gray-300/15 to-slate-300/15 rounded-xl blur-2xl -z-10 transition-all duration-300"
            style={{
              transform: isHovered
                ? "translateY(10px) scale(1.05)"
                : "translateY(20px)",
              opacity: isHovered ? 0.8 : 0.4,
            }}
          />
        )}
      </div>
    </CardPreviewProvider>
  );
};

export default LoyaltyCardContainer;
